import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { parsePrice } from '@utils/price-helpers';
import { addItemToCart, deleteItemFromCart, checkForNonRxDiscount } from '@utils/shopify-helpers';
import { PRODUCT_TYPES, TOOLTIP_COPY } from '@utils/enums';
import LineIcon from '@components/cart/icons/LineIcon';
import TrashIconCart from '@components/cart/icons/TrashIconCart';
import { deleteItemByKey, addItem, setIsLoading } from '@features/cart/cartSlice';
import { useDispatch } from 'react-redux';
import styles from './Upsell.module.scss';
import Tooltip from '@components/common/Tooltip';
import LightBoxButton from '@components/icons/LightBoxButton';
import { toggleIsVisible } from '@utils/ui-utils';
import useWindowDimensions from '@hooks/useWindowDimensions';

const Upsell = ({ recommendation, bundleKey, lozengeMessage, addInfo, itemGroupKey, nonRxGroup, onAddUpsell, minicart }) => {
	const dispatch = useDispatch();
	const [addButtonClass, setAddButtonClass] = useState('');
	const [isUpsellAdded, setIsUpsellAdded] = useState(false);
	const [isLens, setIsLens] = useState(false);
	const { available: isAvailable } = recommendation;
	const { width: viewportWidth } = useWindowDimensions();

	let variantTitle;
	let recommendationID;
	let featuredImage;
	let addLInfo = addInfo;
	const isRecommendationLens = recommendation?.product_type === PRODUCT_TYPES.LENS;
	const isRecommendationTopFrame = recommendation?.product_type?.includes(PRODUCT_TYPES.TOP_FRAME);

	let upsellPrice = nonRxGroup && isRecommendationLens ? 'Free' : `$${parsePrice(recommendation.price, 0)}`;
	if (recommendation.variants) {
		variantTitle = recommendation.title;
		recommendationID = recommendation.variants[0].id;
		featuredImage = recommendation.featured_image;
	} else {
		variantTitle = recommendation.name.includes(' - ') ? recommendation.name.split(' - ')[0] : recommendation.name;
		recommendationID = recommendation.id;
		featuredImage = recommendation.featured_image.src;
	}

	if (isRecommendationLens) {
		if (recommendation.handle === 'blue-light-filtering') {
			addLInfo = 'Recommended for spending time in front of screens.';
			if (nonRxGroup) upsellPrice = 'Free';
		} else if (recommendation.handle === 'premium-ultra-thin') {
			addLInfo = 'Our most ultra-thin and lightweight lenses!';
		}
	}

	const addUpsellAndUpdateCart = async ({ variantId, quantity, bundleKey, itemGroupKey }) => {
		dispatch(setIsLoading({ value: true, key: itemGroupKey }));
		setAddButtonClass('processing');
		const response = await addItemToCart({ variantId, quantity, bundleKey, product: recommendation });

		if (response.status === UNPROCESSABLE_ENTITY) {
			setAddButtonClass('');
			return;
		}

		const addedItem = response.items[0];
		if (addedItem.product_type === PRODUCT_TYPES.LENS) {
			setIsLens(true);
		}

		setIsUpsellAdded(true);
		setAddButtonClass('');
		dispatch(addItem({ newItem: { ...addedItem }, discountPrice: checkForNonRxDiscount(addedItem.handle, nonRxGroup) }));
	};

	const handleOnDeleteItem = async () => {
		dispatch(setIsLoading({ value: true, key: itemGroupKey }));
		await deleteItemFromCart(recommendation.key, 0);
		dispatch(
			deleteItemByKey({
				itemKey: recommendation.key,
				discountPrice: checkForNonRxDiscount(recommendation.handle, nonRxGroup),
			})
		);
	};

	const checkbox = !isUpsellAdded && <div className='checkbox-select flex-shrink-0' />;

	useEffect(() => {
		setIsUpsellAdded(false);
	}, [recommendation]);

	let divClassName = 'd-flex flex-grow-1 justify-content-start';
	if (!isRecommendationLens) {
		divClassName += ' h-100 align-items-center';
	}

	return (
		<div
			className={`col-12 cart-item ${isUpsellAdded ? '' : 'upsell'} item-${recommendationID} upsell-${bundleKey} ${
				isLens ? 'cart-item__lens' : ''
			} ${isRecommendationTopFrame && styles.topFrameUpsell}`}
			{...(isLens ? { 'data-itemkey': recommendation.key } : {})}
			{...(isRecommendationLens
				? {
						onClick: () => {
							isAvailable &&
								addUpsellAndUpdateCart({
									variantId: recommendationID,
									quantity: 1,
									bundleKey,
									itemGroupKey,
								});
						},
				  }
				: {})}
		>
			<div className={divClassName}>
				{isRecommendationLens ? (
					checkbox
				) : (
					<div
						className={`item-image align-self-center flex-column align-items-start cart-item__image flex-shrink-0 position-relative pl-2 pr-2 h-100 ${styles.leftContainer}`}
						onClick={() => viewportWidth < 576 && toggleIsVisible(featuredImage, variantTitle)}
						data-open-lightbox-cart={variantTitle}
					>
						{!isUpsellAdded && <div className={`label flag ${styles.lozengeMsg}`}>{lozengeMessage}</div>}
						<img
							className='img-fluid mx-auto d-block bg-transparent'
							src={featuredImage}
							data-id={recommendationID}
							alt={variantTitle}
						/>
						{isRecommendationTopFrame && (
							<LightBoxButton
								className={styles.lightBoxButton}
								imageSrc={featuredImage}
								imageAlt={variantTitle}
								imageSubtitle={`for The ${
									recommendation.title.includes(' / ')
										? recommendation.title.split(' / ')[0]
										: recommendation.title
								}`}
								width='20'
								height='20'
								price={upsellPrice}
								onLightBoxActionClick={() => {
									isAvailable &&
										addUpsellAndUpdateCart({
											variantId: recommendationID,
											quantity: 1,
											bundleKey,
											itemGroupKey,
										});
								}}
							/>
						)}
					</div>
				)}
				<div
					className={`cart-upsell-container pl-3 h-100 d-flex justify-content-lg-between align-items-lg-start ${
						isRecommendationTopFrame && styles.rightContainer
					} ${isUpsellAdded && 'border-0'}`}
				>
					<div className={`text-left cart-item__title ${styles.lozengeMessage}`}>
						<div className={`d-flex flex-column ${styles.variantTitle}`}>
							<p className='m-0 title' style={{ textAlign: 'start' }}>
								{variantTitle}
							</p>
							{isUpsellAdded && recommendation.product_type.includes('TOP_FRAME') ? (
								<div
									className={`${styles.variant} ${isUpsellAdded ? 'align-self-start p-0' : ''}`}
									data-base-frame-designation={`for-The-${
										recommendation.title.includes(' / ')
											? recommendation.title.split(' / ')[0]
											: recommendation.title
									}`}
								>
									<p className='m-0 cart-title-flex' style={{ textAlign: 'start' }}>
										for The{' '}
										{recommendation.title.includes(' / ')
											? recommendation.title.split(' / ')[0]
											: recommendation.title}
									</p>
								</div>
							) : null}
						</div>
						<div className='p mb-3'>{addLInfo}</div>
					</div>
					<div
						className={`add-control h-100 d-flex flex-column justify-content-end align-self-sm-end ${
							isRecommendationTopFrame && styles.topFrameAddControl
						}`}
					>
						<div className={`position-absolute d-flex flex-grow-0 align-items-center ${styles.upsellPrice}`}>
							{recommendation.handle === 'premium-ultra-thin' && <Tooltip message={TOOLTIP_COPY.ULTRA_THIN} />}
							{isRecommendationLens && (
								<div
									className={`caption item-price flex-fill align-self-center cart-item__quant item-price-${recommendationID}`}
								>
									{upsellPrice}
								</div>
							)}
						</div>
						{!isRecommendationLens && (
							<div
								data-product={JSON.stringify(recommendation)}
								data-cart-add={variantTitle}
								className={`pair-btn--sm pair-btn__secondary ${styles.addButton} ${addButtonClass} ${
									isAvailable ? '' : 'sold-out'
								}`}
								role='button'
								tabIndex={0}
								onKeyUp={e => {
									if (e.key !== 'Enter' || !isAvailable) return;
									addUpsellAndUpdateCart({
										variantId: recommendationID,
										quantity: 1,
										bundleKey,
										itemGroupKey,
									});
								}}
								onClick={() => {
									isAvailable &&
										addUpsellAndUpdateCart({
											variantId: recommendationID,
											quantity: 1,
											bundleKey,
											itemGroupKey,
										});
								}}
							>
								{isAvailable ? '+Add' : 'Sold Out'}
								{isAvailable && <span>{upsellPrice}</span>}
							</div>
						)}
					</div>
				</div>
			</div>
			<div className={`d-flex flex-grow-0 flex-shrink-0 align-items-center delete-control ${styles.upsellAddedContainer}`}>
				{recommendation.handle === 'premium-ultra-thin' && <Tooltip message={TOOLTIP_COPY.ULTRA_THIN} />}
				{isUpsellAdded && (
					<TrashIconCart
						onDelete={() => {
							handleOnDeleteItem(recommendation);
						}}
						dataCart={recommendation.title}
					/>
				)}
				<LineIcon />
				<div className={`button item-price flex-fill align-self-center cart-item__quant item-price-${recommendation.id}`}>
					{upsellPrice}
				</div>
			</div>
		</div>
	);
};

Upsell.propTypes = {
	recommendation: PropTypes.shape({
		price: PropTypes.number,
		variants: PropTypes.array,
		title: PropTypes.string,
		name: PropTypes.string,
		id: PropTypes.number,
		product_type: PropTypes.string,
	}),
	bundleKey: PropTypes.string,
	lozengeMessage: PropTypes.string,
	addInfo: PropTypes.string,
	itemGroupKey: PropTypes.string,
	nonRxGroup: PropTypes.bool,
	minicart: PropTypes.bool,
	onAddUpsell: PropTypes.func,
};

Upsell.defaultProps = {
	minicart: false,
};

export default Upsell;
